import React from 'react';
import { useTranslation } from 'react-i18next';

import { DateStatusProperties } from '../../../../../../interface/timeline.interface';
import CustomTable from '../../../../../../shared-components/custom-table/CustomTable';

interface ConnectivityTableViewProperties {
  data: DateStatusProperties[];
  loading: boolean;
}

/**
 * Format decimal hours to readable string like "X hrs Y mins" or "Z min"
 * @param {number | string | undefined} timeValue - Value to convert
 * @returns {string} Formatted time string
 */
function formatConnectivityHours(timeValue: number | string | undefined): string {
  if (timeValue === undefined || timeValue === null) {
    return '0 min';
  }
  if (typeof timeValue === 'string') {
    if (
      timeValue.includes('min') ||
      timeValue.includes('hr') ||
      (timeValue.includes('h') && timeValue.includes('m') && timeValue.includes('s'))
    ) {
      return timeValue;
    }
    const parsed = parseFloat(timeValue);
    if (Number.isNaN(parsed)) {
      return '0 min';
    }
    const totalMinutes = Math.round(parsed * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) {
      return `${minutes} min`;
    }
    return `${hours} hrs ${minutes} mins`;
  }
  const totalMinutes = Math.round(timeValue * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return `${minutes} min`;
  }
  return `${hours} hrs ${minutes} mins`;
}

/**
 * Connectivity Table View component displaying online/offline history in tabular layout
 * @param {ConnectivityTableViewProperties} properties - Component properties
 * @returns {React.ReactElement} The rendered component
 */
function ConnectivityTableView(
  properties: ConnectivityTableViewProperties,
): React.ReactElement {
  const { data, loading } = properties;
  const { t } = useTranslation('devices');

  /**
   * Render formatted online hours
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderOnlineHours = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{formatConnectivityHours(rowData?.onlineHours)}</p>
  );

  /**
   * Render formatted offline hours
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderOfflineHours = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{formatConnectivityHours(rowData?.offlineHours)}</p>
  );

  /**
   * Render formatted uptime
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderUptime = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{rowData?.uptime || '0.00%'}</p>
  );

  const connectivityColumns = [
    {
      label: t('date', 'Date'),
      accessorKey: 'dateToShow' as keyof DateStatusProperties,
      columnWidth: 200,
    },
    {
      label: t('onlineHours', 'Online hours'),
      accessorKey: 'onlineHours' as keyof DateStatusProperties,
      columnWidth: 200,
      customData: renderOnlineHours,
    },
    {
      label: t('offlineHours', 'Offline hours'),
      accessorKey: 'offlineHours' as keyof DateStatusProperties,
      columnWidth: 200,
      customData: renderOfflineHours,
    },
    {
      label: t('uptime', 'Uptime'),
      accessorKey: 'uptime' as keyof DateStatusProperties,
      columnWidth: 200,
      customData: renderUptime,
    },
  ];

  return (
    <div className="device-table-view-container">
      <CustomTable
        header={connectivityColumns}
        data={data}
        serialNo
        filter={{}}
        filterClearFunction={() => {}}
        filterFunction={() => {}}
        actionButton={false}
        limit={data.length || 31}
        page={1}
        loading={loading}
        totalPages={1}
        totalResults={data.length || 1}
        pageLimitChange={(value: any) => {
          console.log(value);
        }}
        sizeChangeButtonRequired
        disablePagination
        disableHeader
        height={window.innerHeight >= 700 ? 347 : 352}
      />
    </div>
  );
}

export default ConnectivityTableView;
